import { useCallback, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useConnectModal, useOpenModal } from '@rainbow-modules/hooks';
import { RenderIf } from 'react-rainbow-components';
import usePipeline from 'data/firestore/agent/pipeline/use';
import usePipelineStages from 'data/firestore/agent/pipeline/stage/useCollection';
import useOpportunity from 'data/firestore/agent/opportunity/use';
import { EntityGet } from 'data/firestore/types';
import { Opportunity } from 'data/firestore/agent/opportunity/types';
import useSearchFilters from 'hooks/useSearchFilters';
import { Stage } from 'data/firestore/agent/pipeline/stage/types';
import useHttpQuery from 'data/firestore/useHttpQuery';
import { OPPORTUNITY_DETAILS_DRAWER } from '../../constants';
import OpportunityDetails from './opportunityDetails';
import { DrawerGradient, StyledDrawer } from './styled';

type OpportunityDetailsDrawerData = (
    string
    | EntityGet<Opportunity>
    | {
        opportunityId: string;
        activeTab?: 'details' | 'activity' | 'notes' | 'todos';
    }
);

const OpportunityDetailsDrawer = () => {
    const { agentId = '' } = useParams();
    const {
        getValue,
        setValue,
        clear,
    } = useSearchFilters({
        filterNames: ['showOpportunity'],
    });

    const {
        data: opportunityData = {}, isOpen, ...connectedDrawerProps
    } = useConnectModal(OPPORTUNITY_DETAILS_DRAWER);

    const modalData = opportunityData as OpportunityDetailsDrawerData;

    const [openDrawer, closeDrawer] = useOpenModal(OPPORTUNITY_DETAILS_DRAWER);

    const modalOpportunityId = useMemo(
        () => {
            if (typeof modalData === 'string') {
                return modalData;
            }

            if (modalData && typeof modalData === 'object' && 'opportunityId' in modalData) {
                return modalData.opportunityId;
            }

            return (modalData as EntityGet<Opportunity>)?.id || '';
        },
        [modalData],
    );

    const modalActiveTab = useMemo(
        () => (
            modalData && typeof modalData === 'object' && 'activeTab' in modalData
                ? modalData.activeTab
                : undefined
        ),
        [modalData],
    );

    const opportunityId = getValue('showOpportunity') as string;

    const shouldFetchOpportunity = useMemo(
        () => Boolean(
            opportunityId
            && agentId
            && (
                typeof modalData === 'string'
                || (modalData && typeof modalData === 'object' && 'opportunityId' in modalData)
            ),
        ),
        [agentId, opportunityId, modalData],
    );

    const { data: opportunityInfo, isLoading: isLoadingOpportunity } = useOpportunity(
        agentId,
        opportunityId,
        {
            disabled: !shouldFetchOpportunity || !opportunityId,
            track: [agentId, opportunityId],
        },
    );

    const pipelineId = useMemo(
        () => ((
            typeof modalData === 'string'
            || (modalData && typeof modalData === 'object' && 'opportunityId' in modalData)
                ? opportunityInfo
                : modalData
        ) as EntityGet<Opportunity>
        )?.pipelineId || '',
        [modalData, opportunityInfo],
    );

    const {
        data: pipeline,
        isLoading: isLoadingPipeline,
    } = usePipeline(
        agentId,
        pipelineId,
        {
            disabled: !agentId || !pipelineId,
            track: [agentId, pipelineId],
        },
    );

    const shouldFetchPipelineStages = useMemo(
        () => agentId && pipelineId,
        [agentId, pipelineId],
    );

    const {
        data: stages = [],
        isLoading: isLoadingPipelineStages,
    } = usePipelineStages(
        agentId,
        pipelineId,
        {
            disabled: !shouldFetchPipelineStages,
            track: [agentId, pipelineId],
        },
    );

    const {
        data: opportunitySummary,
        isLoading: isLoadingSummary,
        isFetching: isFetchingSummary,
    } = useHttpQuery<{ id: string, summary: string, updatedAt: string }>({
        method: 'POST',
        pathname: `/agents/${agentId}/opportunities/${opportunityId}/summary`,
        queryOptions: {
            enabled: isOpen && !!agentId && !!opportunityId,
            keepPreviousData: false,
            retry: 1,
        },
    });

    const orderedPipelineStages = useMemo(
        () => {
            const pipelineStageOrder = pipeline?.stages || [];

            return pipelineStageOrder.reduce(
                (activeStages: EntityGet<Stage>[], stageId: string) => {
                    const found = stages.find((stage) => stage.id === stageId);
                    if (found) {
                        return [...activeStages, found];
                    }

                    return activeStages;
                },
                [],
            );
        },
        [pipeline?.stages, stages],
    );

    const opportunity = useMemo(
        () => (
            typeof modalData === 'string'
            || (modalData && typeof modalData === 'object' && 'opportunityId' in modalData)
                ? opportunityInfo
                : modalData
        ) as EntityGet<Opportunity> || undefined,
        [modalData, opportunityInfo],
    );

    const handleCloseDrawer = useCallback(
        () => {
            if (isOpen) {
                clear();
                closeDrawer();
            }
        },
        [closeDrawer, isOpen, clear],
    );

    useEffect(() => {
        if (isOpen) {
            const id = modalOpportunityId;
            if (id) {
                setValue('showOpportunity', id);
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, modalOpportunityId]);

    useEffect(() => {
        if (opportunityId && !isOpen) {
            openDrawer({ data: opportunityId });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [opportunityId]);

    const isLoading = isLoadingOpportunity
        || isLoadingPipeline
        || isLoadingPipelineStages
        || isLoadingSummary
        || isFetchingSummary;

    return (
        <StyledDrawer
            {...connectedDrawerProps}
            isOpen={isOpen}
            onRequestClose={handleCloseDrawer}
            hideCloseButton
            slideFrom="right"
            size="medium"
        >
            <DrawerGradient />
            <RenderIf isTrue={isOpen}>
                <OpportunityDetails
                    onRequestClose={handleCloseDrawer}
                    agentId={agentId}
                    pipelineStages={orderedPipelineStages}
                    data={opportunity}
                    initialTab={modalActiveTab}
                    isLoadingSummary={isLoading}
                    summary={opportunitySummary}
                />
            </RenderIf>
        </StyledDrawer>
    );
};

export default OpportunityDetailsDrawer;
